import { useSyntaxHighlighter } from "@/hooks/useSyntaxHighlighter";
import { createStaticJsxRuntime } from "@/lib/syntax-highlighting-utils";
import { cn } from "@/lib/utils";
import { useMemo } from "react";

interface JsonSyntaxDisplayProps {
  data: unknown;
  showRaw: boolean;
}

export function JsonSyntaxDisplay({ data, showRaw }: JsonSyntaxDisplayProps) {
  const jsonString = useMemo(() => {
    try {
      return JSON.stringify(data, null, 2);
    } catch {
      return "[Invalid JSON Object]";
    }
  }, [data]);

  const jsxRuntimeFactory = useMemo(() => createStaticJsxRuntime, []);

  const { processedContent, isReady } = useSyntaxHighlighter({
    language: "json",
    content: jsonString,
    enabled: !showRaw,
    jsxRuntimeFactory,
  });

  if (showRaw) {
    return (
      <pre className="text-xs overflow-x-auto whitespace-pre-wrap font-mono">
        {JSON.stringify(data)}
      </pre>
    );
  }

  // Show processed content if available
  if (processedContent) {
    return processedContent;
  }

  // Fallback to plain text when worker is ready but no processed content
  return (
    <pre
      className={cn(
        "text-xs overflow-x-auto whitespace-pre-wrap font-mono duration-300 transition-opacity",
        isReady ? "opacity-100" : "opacity-0",
      )}
    >
      {jsonString}
    </pre>
  );
}
